import { ForwardedRef, forwardRef, HTMLProps, useState } from "react";
import { ReactComponent as InfoIcon } from "assets/information-circle.svg";
import classNames from "classnames";
import { ConfirmDialog } from "components/Dialog/ConfirmDialog";
import Fade from "components/Transition/Fade";
import { useModifiedTranslation } from "hooks/useModifiedTranslation";

export interface InputProps extends HTMLProps<HTMLInputElement> {
  label: string;
  error?: string;
  tooltipContent?: string;
  icon?: string;
  id?: string;
}

const InputPlain = (
  {
    label,
    className,
    error,
    tooltipContent,
    id,
    ...inputAttributes
  }: InputProps,
  ref: ForwardedRef<HTMLInputElement>
) => {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const { t } = useModifiedTranslation();
  const isDisabled = !!inputAttributes.disabled;
  const isCheckbox = inputAttributes.type === "checkbox";
  return (
    <>
      <label
        id={!id ? undefined : `${id}-label`}
        className={classNames("text-sm font-normal", {
          "text-red-700": !!error,
          "text-primary-40": isDisabled && !error,
        })}
      >
        <div className="flex gap-x-1 items-center">
          {label}
          {tooltipContent && (
            <>
              <div
                id={!id ? undefined : `${id}-tooltipDialogButton`}
                className="cursor-help"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDialogOpen(true);
                }}
              >
                <InfoIcon />
              </div>
            </>
          )}
        </div>
        <input
          id={id}
          ref={ref}
          /* The background/border/text triplet is picked by exactly one of
             error > disabled > default, rather than layering overrides on a
             shared base. Two competing `bg-*` classes on one element are
             resolved by their order in the compiled stylesheet, not by the
             order written here, so mutually exclusive branches are the only
             way to make the state read predictably. */
          className={classNames(
            "block p-2 w-full text-sm border focus:border-primary-400",
            className,
            {
              "bg-gray-50 border-gray-300": !error && !isDisabled,
              "text-red-900 placeholder-red-700 bg-red-50 focus:border-red-500 border-red-500 rounded-lg":
                !!error,
              /* Disabled reads as inert: flatter fill, receding border and
                 dimmed text. Kept at primary-60 rather than lighter because
                 these fields are often pre-filled with a value the user still
                 needs to read. */
              "bg-primary-5 border-primary-10 text-primary-60 placeholder-primary-30":
                isDisabled && !error && !isCheckbox,
              "cursor-not-allowed": isDisabled,
              "text-green-400 w-5 h-5 rounded-full": isCheckbox,
              "rounded-lg": !isCheckbox,
              "text-black": !isCheckbox && !isDisabled,
            }
          )}
          {...inputAttributes}
        />

        <Fade>
          {error && (
            <p
              id={!id ? undefined : `${id}-error`}
              className="mt-1 text-xs text-red-600"
            >
              {error}
            </p>
          )}
        </Fade>
      </label>
      {tooltipContent && (
        <ConfirmDialog
          id={!id ? undefined : `${id}-tooltipDialog`}
          title={t("component.select.dialogTitle")}
          description={tooltipContent}
          cancelButtonText={t("component.select.dialogCloseButtonLabel")}
          isOpen={confirmDialogOpen}
          setIsOpen={setConfirmDialogOpen}
        />
      )}
    </>
  );
};

export const Input = forwardRef(InputPlain);
